import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { dispatch } from 'store';
import { setRemovedExtras } from 'actions/expiryRenewalActions';
import GreenCheckboxList, { Option } from 'components/GreenCheckboxList/GreenCheckboxList';
import { CoverItem } from 'types/expiryRenewal';
import { Store } from 'types/store';
import AddOnPriceFooter from './AddOnPriceFooter';
import CoverItemModal from './CoverItemModal';
import RemoveExtraModal from './RemoveExtraModal';

// A held extra dressed for the checkbox list, keeping its content row for the modals.
type RemovableOption = CoverItem & Option;

/**
 * The breakdown extras the member holds and pays for and could drop, shown with the same
 * tickbox as the add-ons in "what you're not covered for" so both behave alike. The
 * polarity is the mirror image: every removable extra starts ticked, and unticking one is
 * what needs confirming.
 */
const RemovableExtras = () => {
  const { pageModel, monthlyFlex, removedExtras } = useSelector((state: Store) => state?.expiryRenewal);

  const [modalContent, setModalContent] = useState<CoverItem['modal'] | null>(null);
  // The extra the member is dropping, held here until the confirmation modal is resolved.
  const [pendingRemoval, setPendingRemoval] = useState<RemovableOption | null>(null);

  const options: RemovableOption[] = (pageModel?.addonsContent ?? [])
    .filter((item) => item?.isRemovable)
    .map((item) => ({
      ...item,
      id: item.productCode ?? item.header,
      label: item.header,
      ...(item.modal?.link && {
        link: { text: item.modal.link, onClick: () => setModalContent(item.modal) },
      }),
      description: `<div class="py-1">${item.body}</div>`,
      // Price sits below the link, so it travels in the footer slot rather than the description.
      footer: (
        <AddOnPriceFooter
          details={item}
          context={{
            productCode: item.productCode,
            isMonthlyLongerTermPolicy: pageModel?.isMonthlyLongerTermPolicy,
            monthlyFlex,
            coverPeriod: pageModel?.coverPeriod,
          }}
        />
      ),
    }));

  /**
   * Selection is the complement of the removals rather than a stored list of its own, so an
   * extra dropped and then added back leaves no trace, and a "removedExtras" restored from
   * sessionStorage re-ticks the right rows against freshly priced options.
   */
  const selectedOptions = options.filter((option) => !removedExtras.includes(String(option.id)));

  const commitRemovals = (kept: RemovableOption[]) => {
    const dropped = options
      .filter((option) => !kept.some(({ id }) => id === option.id))
      .map((option) => String(option.id));
    dispatch(setRemovedExtras(dropped));
  };

  const handleSetSelected = (updater: (_prev: RemovableOption[]) => RemovableOption[]) => {
    const next = updater(selectedOptions);
    const removed = selectedOptions.find((option) => !next.some(({ id }) => id === option.id));

    // Dropping cover needs confirming; adding one back does not.
    if (removed) {
      setPendingRemoval(removed);
      return;
    }

    commitRemovals(next);
  };

  return (
    <>
      <GreenCheckboxList options={options} selected={selectedOptions} setSelected={handleSetSelected} />
      {modalContent && (
        <CoverItemModal modal={modalContent} onClose={() => setModalContent(null)} />
      )}
      {pendingRemoval && (
        <RemoveExtraModal
          productName={pendingRemoval.label}
          onConfirm={() => {
            commitRemovals(selectedOptions.filter(({ id }) => id !== pendingRemoval.id));
            setPendingRemoval(null);
          }}
          onCancel={() => setPendingRemoval(null)}
        />
      )}
    </>
  );
};

export default RemovableExtras;
